import { useId } from "react"
import { relativeLuminance, SWATCH_INPUT_CLASS } from "./ColorPicker"

// Foreground gradient (experiment, 2026-10-20): the pattern runs from the
// foreground color to a second color, in one of four directions. Off is no
// second color, which is how every code renders today. Free, like the other
// colors.
//
// The server holds both ends to the contrast budget, on the same side of the
// background (QrCode#gradient_within_budget): a gradient that crosses the
// background's lightness inverts part of the code. This warns before that.

const DIRECTIONS = [
  { value: "diagonal",   label: "Diagonal" },
  { value: "horizontal", label: "Across" },
  { value: "vertical",   label: "Down" },
  { value: "radial",     label: "Radial" }
]

// The server's 0.4 budget, plus the side rule (QrCode#gradient_within_budget).
function gradientOK(fg, to, bg) {
  const lt = relativeLuminance(to)
  const lf = relativeLuminance(fg || "#000000")
  const lb = relativeLuminance(bg || "#FFFFFF")
  if (lt == null || lf == null || lb == null) return null
  return Math.abs(lb - lt) >= 0.4 && (lb - lt > 0) === (lb - lf > 0)
}

// A starting end color that reads on the background: plasma on light, mint on
// dark, unless that is already the foreground.
function defaultEnd(fg, bg) {
  const dark = (relativeLuminance(bg || "#FFFFFF") ?? 1) < 0.5
  const pick = dark ? "#86EFAC" : "#7C3AED"
  return (fg || "").toUpperCase() === pick ? (dark ? "#22C55E" : "#111111") : pick
}

function DirectionThumb({ type, from, to }) {
  // Unique per instance: the builder and the edit modal can both be open.
  const id = `tq-grad-thumb-${type}-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`
  const coords = { diagonal: [ 0, 0, 1, 1 ], horizontal: [ 0, 0, 1, 0 ], vertical: [ 0, 0, 0, 1 ] }[type]
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
      <defs>
        {type === "radial" ? (
          <radialGradient id={id} cx="0.5" cy="0.5" r="0.7">
            <stop offset="0" stopColor={from} /><stop offset="1" stopColor={to} />
          </radialGradient>
        ) : (
          <linearGradient id={id} x1={coords[0]} y1={coords[1]} x2={coords[2]} y2={coords[3]}>
            <stop offset="0" stopColor={from} /><stop offset="1" stopColor={to} />
          </linearGradient>
        )}
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" fill={`url(#${id})`} />
    </svg>
  )
}

export default function GradientPicker({ foreground, background, gradientColor, gradientType, onChange }) {
  const on = !!gradientColor
  const from = foreground || "#111111"
  const to = gradientColor || defaultEnd(foreground, background)
  const type = gradientType || "diagonal"
  const ok = on ? gradientOK(foreground, gradientColor, background) : null

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => onChange(e.target.checked
            ? { gradientColor: to, gradientType: type }
            : { gradientColor: "", gradientType: "" })}
          className="w-4 h-4 accent-voltage focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-2"
        />
        Gradient
      </label>

      {on && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="color"
              value={to}
              onChange={(e) => onChange({ gradientColor: e.target.value.toUpperCase(), gradientType: type })}
              aria-label="Gradient end color"
              className={SWATCH_INPUT_CLASS}
            />
            <span className="font-mono text-xs">{to}</span>
          </label>

          <div role="radiogroup" aria-label="Gradient direction" className="flex gap-1.5">
            {DIRECTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                role="radio"
                aria-checked={type === d.value}
                title={d.label}
                onClick={() => onChange({ gradientColor: to, gradientType: d.value })}
                className={
                  "flex flex-col items-center gap-0.5 px-1.5 py-1 rounded border text-[10px] font-semibold transition " +
                  (type === d.value
                    ? "border-voltage bg-voltage/5"
                    : "border-[color:var(--color-border)] dark:border-storm hover:border-voltage")
                }
              >
                <DirectionThumb type={d.value} from={from} to={to} />
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {ok === false && (
        <p className="text-xs text-coal dark:text-cloud bg-warn/10 dark:bg-warn/15 rounded px-3 py-2" aria-live="polite">
          The end color is too close to the background, or on its other side. Pick one that stands out like the foreground does.
        </p>
      )}
    </div>
  )
}
